import { lazy, Suspense, type CSSProperties } from "react";
import {
  usePageLoading,
  usePageLoadingState,
} from "../contexts/page-loading.context";
import { BUBBLE_LOADER_DEFAULTS } from "./BubbleLoader/config";
import type { SpinPhase } from "./BubbleLoader/spin-sequence";
import { Icon } from "./Icon";

const Centrifuge = lazy(() =>
  import("./BubbleLoader/BubbleLoader").then(({ BubbleLoader }) => ({
    default: BubbleLoader,
  })),
);

export type LoadingIndicatorProps = {
  /** Visible logo height in CSS pixels. */
  size?: number;
  style?: CSSProperties;
  className?: string;
  "aria-label"?: string;
  active?: boolean;
  /** The single visible loader in the navigation; does not register itself as pending work. */
  navigation?: boolean;
  onSpinPhaseChange?: (phase: SpinPhase) => void;
};

export function LoadingIndicator({
  size = 32,
  style,
  className,
  "aria-label": label = "Loading",
  active = true,
  navigation = false,
  onSpinPhaseChange,
}: LoadingIndicatorProps) {
  usePageLoading(active && !navigation);
  const { hasNav } = usePageLoadingState();
  if (hasNav && !navigation) {
    return <span role="status" aria-label={label} className="sr-only" />;
  }

  const shift = BUBBLE_LOADER_DEFAULTS.spin.accentShiftPx;
  return (
    <div
      className={className}
      style={{
        display: "inline-block",
        position: "relative",
        width: size * 2 + Math.abs(shift),
        height: size,
        flexShrink: 0,
        verticalAlign: "middle",
        ...style,
      }}
    >
      <Suspense
        fallback={
          <span role="status" aria-label={label}>
            <Icon
              aria-hidden="true"
              style={{ width: "100%", height: "100%" }}
            />
          </span>
        }
      >
        <div
          style={{
            position: "absolute",
            width: size * 2.5,
            height: size * 2.5,
            left: `calc(50% - ${shift / 2}px)`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        >
          <Centrifuge
            variant="spin"
            label={label}
            active={active}
            onSpinPhaseChange={onSpinPhaseChange}
          />
        </div>
      </Suspense>
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 160,
        padding: 24,
      }}
    >
      <LoadingIndicator size={64} />
    </div>
  );
}
